import { useEffect, useRef, useState } from 'react';
import { getPdfJs } from '@utils/pdfjs';
import type { PDFDocumentProxy } from 'pdfjs-dist';

interface IPdfDocumentState {
  pdf: PDFDocumentProxy | null;
  numPages: number;
  error: boolean;
}

/**
 * Loads a PDF blob through pdf.js and exposes the document plus a fit-render
 * helper. The document (and its object URL) live for the blob's lifetime and
 * are destroyed on cleanup, so page flips reuse one parse.
 */
export function usePdfDocument(blob: Blob | null): IPdfDocumentState & {
  renderPage: (
    pageNo: number,
    canvas: HTMLCanvasElement,
    scale: number | null,
  ) => Promise<{ width: number; height: number } | undefined>;
} {
  const [state, setState] = useState<IPdfDocumentState>({ pdf: null, numPages: 0, error: false });
  // Serialises renders: pdf.js refuses concurrent render() calls on one canvas,
  // and a fast prev/next click sequence would otherwise interleave them.
  const renderChain = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    let task: { destroy: () => Promise<void> } | null = null;

    (async () => {
      const pdfjsLib = await getPdfJs();
      // Closed while the library loaded: open nothing, so nothing is left behind.
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      const loading = pdfjsLib.getDocument({ url: objectUrl });
      task = loading;
      const pdf = await loading.promise;
      if (cancelled) return;
      setState({ pdf, numPages: pdf.numPages, error: false });
    })().catch(() => {
      if (!cancelled) setState({ pdf: null, numPages: 0, error: true });
    });

    return () => {
      cancelled = true;
      void task?.destroy().catch(() => {});
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setState({ pdf: null, numPages: 0, error: false });
    };
  }, [blob]);

  /**
   * Renders `pageNo` at `scale` CSS scale (1 = the page's original size),
   * DPR-sharpened. `scale: null` skips rendering and only measures. Either
   * way it resolves with the page's natural (scale-1) CSS dimensions — the
   * caller's basis for fit-percentage math.
   */
  const renderPage = (
    pageNo: number,
    canvas: HTMLCanvasElement,
    scale: number | null,
  ): Promise<{ width: number; height: number } | undefined> => {
    const { pdf } = state;
    if (!pdf) return Promise.resolve(undefined);
    const run = renderChain.current.then(async () => {
      const page = await pdf.getPage(pageNo);
      const natural = page.getViewport({ scale: 1 });
      if (scale !== null) {
        const dpr = window.devicePixelRatio || 1;
        const scaled = page.getViewport({ scale: scale * dpr });
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        canvas.style.width = `${scaled.width / dpr}px`;
        canvas.style.height = `${scaled.height / dpr}px`;
        await page.render({ canvasContext: canvas.getContext('2d')!, canvas, viewport: scaled }).promise;
      }
      return { width: natural.width, height: natural.height };
    });
    renderChain.current = run.then(() => {}).catch(() => {});
    return run;
  };

  return { ...state, renderPage };
}
